import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import ApiKeyCell from "./ApiKeyCell";
import ModalShell, { ModalLoading } from "./Modals/ModalShell";

interface KeyActionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;                       // confirm-view header
  body?: React.ReactNode;              // confirm-view body
  actionLabel: string;                 // e.g. "Generate" / "Revoke"
  destructive?: boolean;               // red action button (revoke)
  loadingLabel: string;                // e.g. "Generating..." / "Revoking..."
  successMessage?: React.ReactNode;    // shown on success when no key is returned
  loading: boolean;                    // from the owning slice
  error: string | null;                // from the owning slice
  /** Runs the mutation; return the minted key to reveal it on success, else void. */
  onConfirm: () => Promise<string | null | void>;
}

/**
 * Shared confirm → loading → error → success modal for API-key generate/revoke. Generate
 * returns the new key (revealed on success); revoke returns nothing (shows successMessage).
 * The slice-specific bits (which thunk, which slice's loading/error) are passed in by the
 * thin per-key-type wrappers.
 */
const KeyActionModal: React.FC<KeyActionModalProps> = ({
  open,
  setOpen,
  title,
  body,
  actionLabel,
  destructive = false,
  loadingLabel,
  successMessage,
  loading,
  error,
  onConfirm,
}) => {
  const { accessToken } = useSelector((state: RootState) => state.user);
  const [submitted, setSubmitted] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);

  const handleReset = () => {
    setSubmitted(false);
    setGeneratedKey(null);
  };

  const handleClose = () => {
    handleReset();
    setOpen(false);
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const key = await onConfirm();
    setGeneratedKey(typeof key === "string" ? key : null);
  };

  const view: "confirm" | "loading" | "error" | "success" =
    !submitted && !loading ? "confirm"
    : loading && !error ? "loading"
    : error ? "error"
    : "success";

  return (
    <ModalShell
      open={open}
      onClose={handleClose}
      size="sm"
      busy={view === "loading"}
      tone={view === "error" ? "error" : view === "success" ? "success" : view === "confirm" && destructive ? "warning" : undefined}
      title={
        view === "confirm" ? title
        : view === "loading" ? loadingLabel
        : view === "error" ? "An error occurred"
        : generatedKey ? "API key generated" : "Success"
      }
      actions={
        view === "confirm" ? (
          <>
            <button type="button" className="btn" onClick={handleClose}>Close</button>
            <button type="button" className={`btn ${destructive ? "btn-danger" : "btn-primary"}`} onClick={handleConfirm} disabled={!accessToken}>{actionLabel}</button>
          </>
        ) : view === "error" ? (
          <>
            <button type="button" className="btn" onClick={handleClose}>Close</button>
            <button type="button" className="btn btn-primary" onClick={handleReset}>Try again</button>
          </>
        ) : view === "success" ? (
          <button type="button" className="btn btn-primary" onClick={handleClose}>OK</button>
        ) : undefined
      }
    >
      {view === "confirm" ? (
        body ? <div>{body}</div> : null
      ) : view === "loading" ? (
        <ModalLoading/>
      ) : view === "error" ? (
        <p>{error}</p>
      ) : generatedKey ? (
        <>
          <p>Copy it now — treat it like a password.</p>
          <div className="dlg-current">
            <span className="dlg-label">API key</span>
            <ApiKeyCell apiKey={generatedKey} />
          </div>
        </>
      ) : successMessage ? (
        <div>{successMessage}</div>
      ) : null}
    </ModalShell>
  );
};

export default KeyActionModal;
